import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Database,
  Loader2,
} from "lucide-react";
import { FC } from "react";
import { FormattedMessage } from "react-intl";
import { IndexStatusResponse } from "~/lib/knowledge/reindex";

interface IndexStatusBadgeProps {
  status: IndexStatusResponse | undefined;
  isLoading?: boolean;
  compact?: boolean;
}

export const IndexStatusBadge: FC<IndexStatusBadgeProps> = ({
  status,
  isLoading,
  compact = false,
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center gap-1.5 text-gray-400 text-xs">
        <Loader2 className="h-3 w-3 animate-spin" />
        {!compact && (
          <span>
            <FormattedMessage id="index.loading" defaultMessage="Loading..." />
          </span>
        )}
      </div>
    );
  }

  if (!status || status.index_status === "not_found") {
    return (
      <div className="flex items-center gap-1.5 text-gray-400 text-xs">
        <Database className="h-3 w-3" />
        {!compact && (
          <span>
            <FormattedMessage
              id="index.notIndexed"
              defaultMessage="Not indexed"
            />
          </span>
        )}
      </div>
    );
  }

  const { index_status, completed_jobs, total_jobs, failed_jobs } = status;

  // Calculate progress percentage on UI from completed/failed jobs
  const progressPercent =
    total_jobs > 0
      ? Math.round(((completed_jobs + failed_jobs) / total_jobs) * 100)
      : 0;

  if (index_status === "indexing") {
    return (
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1.5 text-blue-600 text-xs">
          <Loader2 className="h-3 w-3 animate-spin" />
          <span>
            <FormattedMessage
              id="index.indexing"
              defaultMessage="Indexing..."
            />
          </span>
        </div>
        {total_jobs > 0 && (
          <div className="flex items-center gap-2">
            <div className="w-24 h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-600 rounded-full transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <span className="text-xs text-gray-500">{progressPercent}%</span>
          </div>
        )}
      </div>
    );
  }

  if (index_status === "completed" || index_status === "indexed") {
    return (
      <div className="flex items-center gap-1.5 text-green-600 text-xs">
        <CheckCircle2 className="h-3 w-3" />
        {!compact && (
          <span>
            <FormattedMessage id="index.indexed" defaultMessage="Indexed" />
            {status.documents_loaded > 0 && (
              <span className="text-gray-500 ml-1">
                ({status.documents_loaded} docs)
              </span>
            )}
          </span>
        )}
      </div>
    );
  }

  if (index_status === "failed") {
    return (
      <div className="flex items-center gap-1.5 text-red-600 text-xs">
        <AlertCircle className="h-3 w-3" />
        {!compact && (
          <span>
            <FormattedMessage id="index.failed" defaultMessage="Index failed" />
            {failed_jobs > 0 && (
              <span className="text-gray-500 ml-1">({failed_jobs} errors)</span>
            )}
          </span>
        )}
      </div>
    );
  }

  // pending status
  return (
    <div className="flex items-center gap-1.5 text-gray-500 text-xs">
      <Clock className="h-3 w-3" />
      {!compact && (
        <span>
          <FormattedMessage id="index.pending" defaultMessage="Pending" />
        </span>
      )}
    </div>
  );
};
